/**
 * AGE CERTIFICATIONS — the data is Wikidata's, the chip is ours.
 *
 * `/certs.json` is built by `scripts/fetch-certifications.mjs` from Wikidata
 * (CC0 — public domain, nothing to breach), keyed by IMDb id. Each title maps
 * to ONE code: the original US label where an American body rated it ("PG-13",
 * "R", "TV-MA"), or our own age-tier text ("12+", "15+", "ALL") where the only
 * answer is a foreign one — a British "15 certificate" shown raw would be a
 * foreign body on this UI. The full reasoning, the measured coverage (91% of
 * catalogue movies, ~7% of series — US TV ratings barely exist on Wikidata)
 * and the rejected alternatives live in the script's header.
 *
 * WHY THIS IS A FETCH AND NOT AN IMPORT. The file is ~150 KB of JSON for 8.6k
 * titles — awards ships 17 KB in the bundle and that is right at that size;
 * ten times that does not belong in the JavaScript everyone pays for before
 * first paint. It is a static asset under `web/public`, fetched once the first
 * time any surface asks about any title, and held in memory for the session.
 *
 * THE CONTRACT: `certOf` answers null until the table has landed and for every
 * title the table does not know. A chip is drawn ONLY from a real answer —
 * never invented, never defaulted — which is the same honesty rule the awards
 * badge follows. Callers need no loading state: the module's `$state` means a
 * `$derived` that asked before the table landed simply re-answers when it does.
 */

export interface Cert {
  /** What the chip prints: "PG-13", "TV-MA", "15+", "ALL". */
  label: string;
  /** The tier, as a minimum comfortable age; 0 is "fine for anyone". */
  age: number;
  /** The spelled-out version for a screen reader: "Rated PG-13". */
  spoken: string;
}

/*
 * Every code the data file may contain — the build script holds the same list,
 * and the two must move together. An unknown code answers null rather than a
 * broken chip.
 */
const CERTS: Record<string, Cert> = {
  G: { label: 'G', age: 0, spoken: 'Rated G — general audiences' },
  PG: { label: 'PG', age: 8, spoken: 'Rated PG — parental guidance suggested' },
  'PG-13': { label: 'PG-13', age: 13, spoken: 'Rated PG-13' },
  R: { label: 'R', age: 17, spoken: 'Rated R — restricted' },
  'NC-17': { label: 'NC-17', age: 18, spoken: 'Rated NC-17 — adults only' },
  'TV-Y': { label: 'TV-Y', age: 0, spoken: 'Rated TV-Y — all children' },
  'TV-Y7': { label: 'TV-Y7', age: 7, spoken: 'Rated TV-Y7 — children seven and up' },
  'TV-G': { label: 'TV-G', age: 0, spoken: 'Rated TV-G — general audiences' },
  'TV-PG': { label: 'TV-PG', age: 10, spoken: 'Rated TV-PG — parental guidance suggested' },
  'TV-14': { label: 'TV-14', age: 14, spoken: 'Rated TV-14' },
  'TV-MA': { label: 'TV-MA', age: 17, spoken: 'Rated TV-MA — mature audiences' },
  ALL: { label: 'ALL', age: 0, spoken: 'Rated suitable for all ages' },
  '7+': { label: '7+', age: 7, spoken: 'Rated for ages 7 and up' },
  '10+': { label: '10+', age: 10, spoken: 'Rated for ages 10 and up' },
  '12+': { label: '12+', age: 12, spoken: 'Rated for ages 12 and up' },
  '13+': { label: '13+', age: 13, spoken: 'Rated for ages 13 and up' },
  '14+': { label: '14+', age: 14, spoken: 'Rated for ages 14 and up' },
  '15+': { label: '15+', age: 15, spoken: 'Rated for ages 15 and up' },
  '16+': { label: '16+', age: 16, spoken: 'Rated for ages 16 and up' },
  '18+': { label: '18+', age: 18, spoken: 'Rated for ages 18 and up' }
};

/* The table, once it lands. An object holder so the module can export state. */
const store = $state<{ table: Record<string, string> | null }>({ table: null });

/** One fetch per session, however many surfaces ask. NOT reactive on purpose —
 * `certOf` is called inside `$derived` and a reactive guard it also wrote
 * would re-run the very computation that set it. */
let asked = false;

async function load(): Promise<void> {
  try {
    const res = await fetch('/certs.json', { headers: { accept: 'application/json' } });
    if (!res.ok) return; // no chip is the designed absence state; nothing to unwind
    store.table = (await res.json()) as Record<string, string>;
  } catch {
    /* Offline or blocked: the chip simply never appears this session. */
  }
}

/**
 * The certification for one title, or null — while loading, when unrated, when
 * offline. First call triggers the fetch; a `$derived` reading this re-answers
 * by itself when the table lands, because it read `store.table`.
 */
export function certOf(imdbId: string | undefined): Cert | null {
  if (!asked) {
    asked = true;
    void load();
  }
  if (!imdbId) return null;
  const code = store.table?.[imdbId];
  return code !== undefined ? (CERTS[code] ?? null) : null;
}
